export default class Helpers {

  /**
   * Filter support function for returning unique properties
   *
   * @param value - filter value
   * @param index - filter index
   * @param self  - callback
   * @returns {boolean} - unique value
   */
  static onlyUnique( value, index, self ) {
    return self.indexOf( value ) === index;
  }

  /**
   * Tests if all purposes in the consent object are true
   *
   * @param obj - consent object
   * @returns {boolean} - true/false
   */
  static consentPurposesAllTrue( obj ) {
    for ( const o in obj ) {
      if ( ! obj[o] ) return false;
    }
    return true;
  }

  /**
   * Calculates if personalised advertising is allowed
   *
   * @param tcData - user consent data object
   * @returns {Promise<boolean>} - true/false
   */
  static async isPersonalisedAllowed( tcData ) {
    if ( tcData && tcData.hasOwnProperty( 'purpose' ) &&
      tcData.purpose.hasOwnProperty( 'consents' ) &&
      this.consentPurposesAllTrue( tcData.purpose.consents )
    ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Calculates the current viewport width and height
   *
   * @returns {{width: number, height: number}}
   */
  static getViewportDimentions() {
    return {
      height: Math.max( document.documentElement.clientHeight || 0, window.innerHeight || 0 ),
      width: Math.max( document.documentElement.clientWidth || 0, window.innerWidth || 0 )
    };
  }

  /**
   * Tests if 2 javascript objects are equal
   *
   * @param x - object 1
   * @param y - object 2
   * @returns {Promise<boolean>} - true/false
   */
  static async objectsEqual( x, y ) {
    // If both x and y are null or undefined and exactly the same
    if ( x === y ) return true;

    // If they are not strictly equal, they both need to be Objects
    if ( ! (x instanceof Object) || ! (y instanceof Object) ) return false;

    // They must have the exact same prototype chain, the closest we can do is test there constructor.
    if ( x.constructor !== y.constructor ) return false;

    for ( const p in x ) {
      // Other properties were tested using x.constructor === y.constructor
      if ( ! x.hasOwnProperty( p ) ) continue;

      // Allows to compare x[ p ] and y[ p ] when set to undefined
      if ( ! y.hasOwnProperty( p ) ) return false;

      // If they have the same strict value or identity then they are equal
      if ( x[p] === y[p] ) continue;

      // Numbers, Strings, Functions, Booleans must be strictly equal
      if ( typeof (x[p]) !== 'object' ) return false;

      // Objects and Arrays must be tested recursively
      if ( ! await this.objectsEqual( x[p], y[p] ) ) return false;
    }

    // Allows x[ p ] to be set to undefined
    for ( const p in y ) {
      if ( y.hasOwnProperty( p ) && ! x.hasOwnProperty( p ) ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Tests whether the TCF (Transparency and Consent Framework) API is ready
   *
   * @param timeout - the amount of time to wait for the API before rejecting
   * @returns {Promise<unknown>} - resolves the promise when loaded or when the timeout is reached
   */
  static tcfLoaded( timeout ) {
    return new Promise( ( resolve, reject ) => {
      let check = () => {
        if ( typeof window.__tcfapi !== 'undefined' ) {
          resolve( true );
        } else if ( (timeout -= 100) < 0 ) {
          resolve( false );
        } else {
          setTimeout( check, 100 );
        }
      };
      setTimeout( check, 100 );
    } );
  };

  /**
   * Tests whether the GPT (Google Publisher Tag) API is ready
   *
   * @param timeout - the amount of time to wait for the API before rejecting
   * @returns {Promise<unknown>} - resolves the promise when loaded or when the timeout is reached
   */
  static googletagLoaded( timeout ) {
    return new Promise( ( resolve, reject ) => {
      let check = () => {
        if ( typeof googletag !== 'undefined' && typeof googletag.pubads === 'function' ) {
          resolve( true );
        } else if ( (timeout -= 100) < 0 ) {
          resolve( false );
        } else {
          setTimeout( check, 100 );
        }
      };
      setTimeout( check, 100 );
    } );
  };

  /**
   * Listens for the TCF API event status and resolves when loaded or when the user accepts the consent popup
   *
   * @returns {Promise<unknown>} - resolves when the expected event status is detected
   */
  static tcfDataReady() {
    return new Promise( ( resolve ) => {
	    if ( typeof window.__tcfapi !== 'undefined' ) {
		    window.__tcfapi( 'addEventListener', 2, function ( tcData, listenerSuccess ) {
			    // If the lister is successful and either the event "tcLoaded" or "useractioncomplete" has fired proceed
			    if ( listenerSuccess && (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') ) {
				    resolve( tcData );
			    }
		    } );
	    }
    } );
  };

  /**
   * Resets the last user activity tracking on user activity
   */
  static userActivity() {
    window.userLastActivity = Math.floor( Date.now() / 1000 );
  }

  /**
   * Tests if the user is active
   *
   * @returns {boolean}
   */
  static userIsActive() {
    if ( document.visibilityState === 'visible' && ((Math.floor( Date.now() / 1000 ) - window.userLastActivity) < window.userActivityTimeout) ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Tests if an element is entering the viewport
   *
   * @param elementSelector - the element selector
   * @param callback - the callback function to fire when the element is entering
   * @param offSet - the amount of leading space before in view detection
   */
  static observedElementInView( elementSelector, callback, offSet ) {
    let wHeight            = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    let viewLazyLoadOffset = Math.round( wHeight * 0.75 ) || 1000;
    if ( offSet > 1 ) {
      // Amount in pixels
      viewLazyLoadOffset = offSet;
    } else if ( offSet > 0 ) {
      // Percentage of view heights
      viewLazyLoadOffset = Math.round( wHeight * offSet ) || 1000;
    }
    const ioConfig = {rootMargin: viewLazyLoadOffset + 'px'};
    let observer   = new IntersectionObserver( function ( entries, self ) {
      entries.forEach( entry => {
        if ( entry.isIntersecting ) {
          callback( entry.target );
          self.unobserve( entry.target );
        }
      } );
    }, ioConfig );
    const elems    = document.querySelectorAll( elementSelector );
    elems.forEach( elem => {
      observer.observe( elem );
    } );
  }

  /**
   * Defers running a particular piece of code until jQuery is loaded
   *
   * @param method - the function to defer
   */
  static defer( method ) {
    if ( window.jQuery ) {
      method();
    } else {
      setTimeout( function () {
        defer( method );
      }, 50 );
    }
  }

  /**
   * Initiates the legacy takeover logic, this function is called from within
   * the home page takeover creative template in DFP.
   *
   * When called it first collapses any slots marked with the "takeover-collapse"
   * css class right away and then builds the takeover and tracking pixel based on
   * the data passed in from the creative.
   *
   * Finally it triggers the legacy jQuery "takeover_rendered" event which the themes
   * listen for and when detected proceed with the required dom manipulations to support the takeover.
   * The function is deferred until jQuery is loaded in order to support this.
   *
   * @param data - the home page takeover creative data object
   */
  static legacyTakeover( data ) {

    // Collapse any slots with the "takeover-collapse" css class right away (prevents visible layout shifting when the takeover is rendered)
    let collapseEarly = document.getElementsByClassName( 'takeover-collapse' );
    for ( let i = 0; i < collapseEarly.length; i++ ) {
      collapseEarly[i].style.display = 'none';
    }

    // defer the takeover logic until jquery is ready in order to support the existing integration with the themes
    this.defer( function () {
      (function ( $ ) {
        $( document ).ready( function () {
          let $body = $( 'body' );
          let $link = $( '<a class="takeover" href="' + data.url + '" target="_blank"></a>' );
          $link.css( 'background-image', 'url(' + data.image + ')' );
          if ( data.impressiontracker && '' !== $.trim( data.impressiontracker ) ) {
            $body.prepend( '<img src="' + data.impressiontracker + '" style="display:none;" />' );
          }

          $body.prepend( $link )
	        .ready(() => {
		      // takeover rendered jquery event is then used by themes to do further layout work
		      $( window.dfp ).trigger( 'takeover_rendered' );
		    });
		} );

      })( jQuery );
    } );
  }

  /**
   * Checks if a particular container has children
   *
   * @param selector - Container CSS Selector
   * @returns {Promise<Boolean>}
   */
  static async hasChildren( container ) {
    return container.hasChildNodes() ? true : false;
  }

}